import axios from 'axios';
import get from 'lodash/get';
import {getUrl} from '@/config/api.routes';

const basePath = 'aimanager.forms';
const Q2_PROVIDER = 'q2';

export default {
  namespaced: true,
  state: {
    agentForms: [],
    agentFormItem: null,
  },
  getters: {
    getAgentForms(state) {
      return state.agentForms;
    },
    getAgentFormItem(state) {
      return state.agentFormItem;
    },
  },
  mutations: {
    SET_AGENT_FORMS(state, agentForms) {
      state.agentForms = agentForms;
    },
    APPEND_AGENT_FORMS(state, agentForms) {
      state.agentForms = [...state.agentForms, ...agentForms];
    },
    SET_AGENT_FORM_ITEM(state, agentFormItem) {
      state.agentFormItem = agentFormItem;
    },
  },
  actions: {

    // GET /forms
    // List all forms with their latest extract/match status.
    async listForms({ commit }, payload = {}) {
      const { limit, offset, q } = payload;
      const response = await axios.get(getUrl(`${basePath}`), {
        params: { limit, offset, q },
      });
      const models = get(response, 'data.data', []);
      // offset 0 is a fresh load/search; later offsets are infinite-scroll pages.
      commit(offset ? 'APPEND_AGENT_FORMS' : 'SET_AGENT_FORMS', models);
      return response;
    },

    // GET /forms/:id
    // Fetch a single form. Pass return_fields=true to embed its fields.
    async getForm({ commit }, payload) {
      const { id, return_fields = true } = payload;
      const response = await axios.get(`${getUrl(`${basePath}`)}/${id}`, {
        params: { return_fields },
      });
      commit('SET_AGENT_FORM_ITEM', response.data);
      return response;
    },

    // GET /forms/provider_schema/:provider
    // Return the source-entity bundle for a provider (e.g. q2) used for field matching.
    async getFormProviderSchema(_, payload) {
      const { provider = Q2_PROVIDER } = payload;
      const response = await axios.get(getUrl(`${basePath}/provider_schema/${provider}`));
      return response;
    },

    // POST /forms (multipart, uploaded file)
    // Create a form from an uploaded PDF and start extraction via agentic-forms.
    // Select a PDF for the 'form' field before sending.
    async createFormFromFile(_, payload) {
      const response = await axios.post(getUrl(`${basePath}`), payload);
      return response;
    },

    // PUT /forms/:id
    // Update a form's title and/or draft flag. Publish a form by setting draft=false.
    async updateForm(_, payload) {
      const { id, body } = payload; // { title, draft }

      const response = await axios.put(`${getUrl(`${basePath}`)}/${id}`, body);
      return response;
    },

    // POST /forms/:id/match
    // Kick off field-to-source matching for the form using the given provider's bundle.
    // Requires extraction to have populated schema_json.
    async startFormMatch(_, payload) {
      const { id, provider = Q2_PROVIDER } = payload;
      const response = await axios.post(`${getUrl(`${basePath}`)}/${id}/match`, { provider });
      return response;
    },

    // POST /forms/:id/create_entities
    // Generate the per-field entity mappings from the form's questions. Requires
    // the extract workflow to be completed. Returns the created field->keys map.
    async createFormEntities(_, payload) {
      const { id } = payload;
      const response = await axios.post(`${getUrl(`${basePath}`)}/${id}/create_entities`);
      return response;
    },

    // POST /forms/:id/artifacts
    // Compile the form into its IQtools artifacts (entities, dialogs, workflow,
    // fill action). Uses the endpoints stored on the form at upload. Requires
    // extract and match to be completed and draft=false.
    async compileFormWorkflow(_, payload) {
      const { id } = payload;
      const response = await axios.post(`${getUrl(`${basePath}`)}/${id}/artifacts`);
      return response;
    },

    // DELETE /forms/:id/artifacts
    // Tear down the form's generated IQtools artifacts (entities, dialogs,
    // workflow, fill action) while keeping the form itself.
    async deleteFormArtifacts(_, payload) {
      const { id } = payload;
      const response = await axios.delete(`${getUrl(`${basePath}`)}/${id}/artifacts`);
      return response;
    },

    // POST /forms/:form_key/fill
    // Fill the form's PDF from collected entities and upload the result to base-api.
    // Returns { bucket, key } (and base64 when return_base64=true). Normally called
    // by base-api's conversations.form.fill command.
    async fillForm(_, payload) {
      const { formKey, entities, return_base64 } = payload;
      const response = await axios.post(`${getUrl(`${basePath}`)}/${formKey}/fill`, {
        entities,
        return_base64: return_base64 ?? false,
      });
      return response;
    },

    // DELETE /forms/:id
    // Delete a form and its generated dialogs, workflows and per-form entities.
    async deleteForm(_, payload) {
      const { id } = payload;
      const response = await axios.delete(`${getUrl(`${basePath}`)}/${id}`);
      return response;
    },

    // POST /forms/:form_key/workflow_callback (internal)
    // Called by agentic-forms when an extract or match workflow finishes. Included
    // for reference/testing; normally invoked by the engine, not manually. Uses a
    // callback token issued by ai-manager rather than the standard bearer token.
    async formWorkflowCallback(_, payload) {
      const { formKey, callbackToken, ...body } = payload; // { workflow_id, status, fields }
      const response = await axios.post(
        `${getUrl(`${basePath}`)}/${formKey}/workflow_callback`,
        body,
        { headers: { Authorization: `Bearer ${callbackToken}` } },
      );
      return response;
    },



    // GET /forms/:form_id/fields
    // List a form's fields with mapping and match metadata.
    async listFormFields(_, payload) {
      const { formId } = payload;
      const response = await axios.get(`${getUrl(`${basePath}`)}/${formId}/fields`);
      return response;
    },

    // POST /forms/:form_id/fields
    // Add a field to a form. field_key is generated server-side from field_name.
    // field_type is one of: text, checkbox, radio, date, currency, phone, email,
    // account_number, routing_number, signature, initials, table_cell, computed,
    // internal_only.
    async addFormField(_, payload) {
      const { formId, ...body } = payload; // { field_name, field_type, required, mapped_keys, needs_review, match_status }
      const response = await axios.post(`${getUrl(`${basePath}`)}/${formId}/fields`, body);
      return response;
    },

    // PUT /forms/:form_id/fields/:field_id
    // Update a form field's name/type/required/mapped_keys/match_status.
    async updateFormField(_, payload) {
      const { formId, fieldId, ...body } = payload;
      const response = await axios.put(
        `${getUrl(`${basePath}`)}/${formId}/fields/${fieldId}`,
        body,
      );
      return response;
    },

    // DELETE /forms/:form_id/fields/:field_id
    // Delete a single form field.
    async deleteFormField(_, payload) {
      const { formId, fieldId } = payload;
      const response = await axios.delete(`${getUrl(`${basePath}`)}/${formId}/fields/${fieldId}`);
      return response;
    },

    // GET /forms/:form_id/questions
    // List a form's questions, ordered by position. Fields link back via
    // form_fields.question_ref === question.id (ordered by question_position).
    async listFormQuestions(_, payload) {
      const { formId } = payload;
      return await axios.get(`${getUrl(`${basePath}`)}/${formId}/questions`);
    },

    // POST /forms/:form_id/questions
    // Create a manual question over EXISTING fields. Requires prompt, answer_type,
    // interaction_class and a non-empty target_field_ids. Optional group_type.
    async addFormQuestion(_, payload) {
      const { formId, ...body } = payload;
      return await axios.post(`${getUrl(`${basePath}`)}/${formId}/questions`, body);
    },

    // PUT /forms/:form_id/questions/:question_id
    // Update a question's prompt/required/interaction_class and/or its position
    // (0-based index) to reorder the question list. question_id is the DB id.
    async updateFormQuestion(_, payload) {
      const { formId, questionId, ...body } = payload;
      return await axios.put(
        `${getUrl(`${basePath}`)}/${formId}/questions/${questionId}`,
        body,
      );
    },

    // DELETE /forms/:form_id/questions/:question_id
    // Delete a single question (scrubbed from schema_json, then re-projected).
    async deleteFormQuestion(_, payload) {
      const { formId, questionId } = payload;
      return await axios.delete(
        `${getUrl(`${basePath}`)}/${formId}/questions/${questionId}`,
      );
    },
  },
};
